import { Link, rootRouteId, useMatch, useRouter } from '@tanstack/react-router'
import type { ErrorComponentProps } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useState, Component } from 'react'
import type { ReactNode } from 'react'

// Thin class-component guard so that if any hook inside DefaultCatchBoundary
// throws (e.g. useTranslation before I18nextProvider is ready, or useMatch in
// an unexpected context) we still show a recovery UI instead of a blank screen.
class CatchBoundaryGuard extends Component<
  { children: ReactNode },
  { crashed: boolean; message: string }
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { crashed: false, message: '' }
  }
  static getDerivedStateFromError(err: unknown) {
    return {
      crashed: true,
      message: err instanceof Error ? err.message : 'An unexpected error occurred',
    }
  }
  render() {
    if (this.state.crashed) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0f', color: '#fff', fontFamily: 'monospace', flexDirection: 'column', gap: 16, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48 }}>⚠️</div>
          <h2 style={{ fontSize: 20, margin: 0 }}>Something went wrong</h2>
          <p style={{ color: '#a0a0b0', margin: 0, maxWidth: 360 }}>{this.state.message}</p>
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: 8, padding: '8px 24px', background: '#00f0ff', color: '#0a0a0f', border: 'none', cursor: 'pointer', fontFamily: 'monospace', fontWeight: 'bold' }}
          >
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

/** Detect tRPC error code from the client error shape */
function getTRPCCode(error: unknown): string | null {
  if (error && typeof error === 'object') {
    const e = error as Record<string, unknown>
    // TRPCClientError exposes data.code
    if (e['data'] && typeof e['data'] === 'object') {
      const code = (e['data'] as Record<string, unknown>)['code']
      if (typeof code === 'string') return code
    }
    // Fallback: check shape field used by some tRPC versions
    if (typeof e['shape'] === 'object' && e['shape'] !== null) {
      const shape = e['shape'] as Record<string, unknown>
      if (typeof shape['code'] === 'string') return shape['code']
    }
  }
  return null
}

/** Extract a Xeito error code from the structured error if present */
function getXeitoCode(error: unknown): string | null {
  if (error && typeof error === 'object') {
    const e = error as Record<string, unknown>
    if (e['xeito'] && typeof e['xeito'] === 'object') {
      const xeito = e['xeito'] as Record<string, unknown>
      if (typeof xeito['code'] === 'string') return xeito['code']
    }
    if (typeof e['message'] === 'string') {
      const match = e['message'].match(/XEITO_[A-Z]+_\d{3}/)
      if (match) return match[0]
    }
  }
  return null
}

type ErrorKind = 'unauthorized' | 'network' | 'notFound' | 'generic'

function classifyError(error: unknown): ErrorKind {
  const trpcCode = getTRPCCode(error)
  if (trpcCode === 'UNAUTHORIZED' || trpcCode === 'FORBIDDEN') return 'unauthorized'
  if (trpcCode === 'NOT_FOUND') return 'notFound'
  if (error instanceof Error) {
    const msg = error.message.toLowerCase()
    if (msg.includes('network') || msg.includes('fetch') || msg.includes('timeout') || msg.includes('connection')) {
      return 'network'
    }
    if (msg.includes('unauthorized') || msg.includes('unauthenticated')) return 'unauthorized'
    if (msg.includes('not found')) return 'notFound'
  }
  return 'generic'
}

function DefaultCatchBoundaryInner({ error }: ErrorComponentProps) {
  const router = useRouter()
  const { t } = useTranslation()
  const isRoot = useMatch({ strict: false, select: (state) => state.id === rootRouteId })
  const [copied, setCopied] = useState(false)

  const kind = classifyError(error)
  const xeitoCode = getXeitoCode(error)
  const trpcCode = getTRPCCode(error)
  // Reference code shown to users for support — prefer Xeito code, fall back to tRPC code
  const supportCode = xeitoCode ?? (trpcCode ? `TRPC_${trpcCode}` : null)

  // Log with context for dev debugging
  console.error(`[DefaultCatchBoundary] kind=${kind} code=${supportCode ?? 'none'}`, error)

  const copyCode = () => {
    if (!supportCode) return
    navigator.clipboard.writeText(supportCode).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (kind === 'unauthorized') {
    return (
      <div className="min-w-0 flex-1 p-8 flex flex-col items-center justify-center gap-4 text-center">
        <div className="text-5xl">🔒</div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('errors.sessionExpiredTitle')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-sm">
          {t('errors.sessionExpiredDescription')}
        </p>
        <Link
          to="/"
          className="mt-2 px-5 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-md text-sm font-medium transition-colors"
        >
          {t('errors.signInButton')}
        </Link>
      </div>
    )
  }

  if (kind === 'notFound') {
    return (
      <div className="min-w-0 flex-1 p-8 flex flex-col items-center justify-center gap-4 text-center">
        <div className="text-6xl font-bold text-gray-200 dark:text-gray-700 select-none">404</div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('errors.notFoundTitle')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-sm">
          {t('errors.notFoundDescription')}
        </p>
        <div className="flex gap-3 flex-wrap justify-center mt-2">
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            {t('errors.errorBoundary.backButton')}
          </button>
          <Link
            to="/"
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-md text-sm font-medium transition-colors"
          >
            {t('errors.errorBoundary.homeButton')}
          </Link>
        </div>
      </div>
    )
  }

  if (kind === 'network') {
    return (
      <div className="min-w-0 flex-1 p-8 flex flex-col items-center justify-center gap-4 text-center">
        <div className="text-5xl">📡</div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('errors.networkErrorTitle')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-sm">
          {t('errors.networkErrorDescription')}
        </p>
        <button
          onClick={() => router.invalidate()}
          className="mt-2 px-5 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-md text-sm font-medium transition-colors"
        >
          {t('errors.errorBoundary.retryButton')}
        </button>
      </div>
    )
  }

  // Generic / server error
  return (
    <div className="min-w-0 flex-1 p-8 flex flex-col items-center justify-center gap-4 text-center">
      <div className="text-5xl">⚠️</div>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
        {t('errors.errorBoundary.title')}
      </h2>
      <p className="text-gray-500 dark:text-gray-400 max-w-sm">
        {t('errors.errorBoundary.description')}
      </p>

      {supportCode && (
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-md text-sm font-mono">
          <span className="text-gray-500 dark:text-gray-400">{t('errors.errorBoundary.errorCode')}:</span>
          <span className="text-gray-900 dark:text-gray-100 select-all">{supportCode}</span>
          <button
            onClick={copyCode}
            title="Copy error code"
            className="ml-1 text-gray-400 hover:text-cyan-600 transition-colors"
            aria-label="Copy error code"
          >
            {copied ? (
              <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </button>
        </div>
      )}

      <p className="text-xs text-gray-400 dark:text-gray-500 max-w-xs">
        {t('errors.errorBoundary.supportText')}
      </p>

      <div className="flex gap-3 flex-wrap justify-center mt-2">
        <button
          onClick={() => router.invalidate()}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-md text-sm font-medium transition-colors"
        >
          {t('errors.errorBoundary.retryButton')}
        </button>
        {isRoot ? (
          <Link
            to="/"
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            {t('errors.errorBoundary.homeButton')}
          </Link>
        ) : (
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            {t('errors.errorBoundary.backButton')}
          </button>
        )}
      </div>
    </div>
  )
}

// Wrap with CatchBoundaryGuard so that any hook failure inside the inner
// component (useTranslation, useRouter, useMatch) still shows a recovery UI
// instead of a blank screen.
export function DefaultCatchBoundary(props: ErrorComponentProps) {
  return (
    <CatchBoundaryGuard>
      <DefaultCatchBoundaryInner {...props} />
    </CatchBoundaryGuard>
  )
}
